import type { WorkerPool } from '@/plugins/pointCloud/core/WorkerPool'
import { workerPool } from '@/plugins/pointCloud/loaders/global'
import type { PotreeConfig } from '@/plugins/pointCloud/loaders/types'
import type { PointCloudWorkerMode } from '@/plugins/pointCloud/types'
import type { LoadingCounter, NodeLoaderDeps } from './OctreeLoader'

/** 缓存内联 Worker 的 Blob URL */
let _inlineBlobUrl: string | undefined

/** Worker 内联 Blob URL 加载 Promise（避免重复加载） */
let _inlineWorkerUrlPromise: Promise<string> | null = null

const DEFAULT_WORKER_FILE = '/workers/PotreeV2DecoderWorker.js'

function resolveBaseUrl(baseUrl?: string): string | undefined {
  if (baseUrl) {
    return baseUrl.replace(/\/+$/, '')
  }

  if (typeof window !== 'undefined' && window.POTREE_BASE_URL) {
    return window.POTREE_BASE_URL.replace(/\/+$/, '')
  }

  return undefined
}

function resolveExternalWorkerUrl(baseUrl?: string): string {
  const normalizedBaseUrl = resolveBaseUrl(baseUrl)
  if (normalizedBaseUrl) {
    return `${normalizedBaseUrl}${DEFAULT_WORKER_FILE}`
  }

  if (typeof window !== 'undefined') {
    return `${window.location.origin}${DEFAULT_WORKER_FILE}`
  }

  return DEFAULT_WORKER_FILE
}

function resolveWorkerMode(config?: PotreeLoaderConfig): PointCloudWorkerMode {
  if (config?.workerMode) {
    return config.workerMode
  }

  if (typeof window !== 'undefined' && window.POTREE_WORKER_MODE) {
    return window.POTREE_WORKER_MODE
  }

  return 'external'
}

function loadInlineWorkerUrl(): Promise<string> {
  if (_inlineBlobUrl) {
    return Promise.resolve(_inlineBlobUrl)
  }

  if (!_inlineWorkerUrlPromise) {
    _inlineWorkerUrlPromise = (async () => {
      const mod = await import('@sharefe/share3d-engine/worker-code')

      // 当前 worker 构建产物仍依赖 import.meta.url，不能安全运行在 Blob Worker 中。
      if (mod.WORKER_CODE.includes('import.meta.url')) {
        throw new Error(
          '当前 Worker 构建依赖 import.meta.url，不能使用内联 Blob 模式，请改用外部 worker 文件模式'
        )
      }

      const blob = new Blob([mod.WORKER_CODE], { type: 'text/javascript' })
      _inlineBlobUrl = URL.createObjectURL(blob)
      return _inlineBlobUrl
    })().catch((error) => {
      _inlineWorkerUrlPromise = null
      throw error
    })
  }

  return _inlineWorkerUrlPromise
}

/**
 * 异步获取 Worker URL。
 * 默认返回外部 Worker 文件 URL；仅在显式启用 inline 模式时尝试创建 Blob URL。
 */
function loadWorkerUrl(config?: PotreeLoaderConfig): string | Promise<string> {
  if (resolveWorkerMode(config) === 'inline') {
    return loadInlineWorkerUrl()
  }

  return resolveExternalWorkerUrl(config?.workerBaseUrl)
}

/** 创建默认 PotreeConfig，每次调用返回独立实例避免多 loader 共享状态 */
function createDefaultConfig(): PotreeConfig {
  return {
    numNodesLoading: 0,
    maxNodesLoading: 8,
  }
}

type PotreeLoaderConfig = PotreeConfig &
  Partial<{
    workerPool: WorkerPool
    workerUrl: string
    workerBaseUrl: string
    workerMode: PointCloudWorkerMode
  }>

/** 外部注入的 Potree 加载配置源 */
type PotreeConfigSource = {
  potreeInstance?: PotreeLoaderConfig
}

/**
 * 创建 OctreeLoader 所需的依赖对象。
 *
 * @param configSource - 可选外部配置源，含 workerPool/workerUrl 等
 * @returns NodeLoaderDeps，含 workerPool、workerUrl、counter、potreeConfig
 */
export function createOctreeLoaderDeps(configSource?: PotreeConfigSource): NodeLoaderDeps {
  const potreeConfig = configSource?.potreeInstance ?? createDefaultConfig()
  potreeConfig.numNodesLoading ??= 0
  potreeConfig.maxNodesLoading ??= 8

  const counter: LoadingCounter = {
    increment() {
      potreeConfig.numNodesLoading++
    },
    decrement() {
      potreeConfig.numNodesLoading--
    },
  }

  return {
    workerPool: configSource?.potreeInstance?.workerPool ?? workerPool,
    workerUrl:
      configSource?.potreeInstance?.workerUrl ?? loadWorkerUrl(configSource?.potreeInstance),
    counter,
    potreeConfig,
  }
}
